import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { paymentProviderApi, type PaymentProviderData } from '../../services/paymentProviderApi';

const PROVIDER_TYPES = [
  { value: 'vakifbank', label: 'Vakıfbank Ortak Ödeme' },
  { value: 'iyzico', label: 'İyzico' },
  { value: 'garanti', label: 'Garanti Sanal POS' },
  { value: 'paytr', label: 'PayTR' },
  { value: 'paynkolay', label: 'Paynkolay Sanal POS' },
];

interface ConfigField {
  key: string;
  label: string;
  placeholder: string;
  type?: string;
  optional?: boolean;
}

const VAKIFBANK_FIELDS: ConfigField[] = [
  { key: 'hostMerchantId', label: 'Host Merchant ID', placeholder: 'Üye işyeri numarası' },
  { key: 'merchantPassword', label: 'Merchant Password', placeholder: 'Üye işyeri şifresi', type: 'password' },
  { key: 'hostTerminalId', label: 'Host Terminal ID', placeholder: 'Terminal numarası' },
];

const IYZICO_FIELDS: ConfigField[] = [
  { key: 'apiKey', label: 'API Key', placeholder: 'API anahtarı' },
  { key: 'secretKey', label: 'Secret Key', placeholder: 'Gizli anahtar', type: 'password' },
];

const PAYNKOLAY_FIELDS: ConfigField[] = [
  { key: 'sx', label: 'Token (sx) Değeri', placeholder: 'Paynkolay panelinden alınan ödeme sx değeri' },
  { key: 'cancelSx', label: 'İptal/İade Değeri', placeholder: 'İptal/iade servisi için ayrı verilen sx değeri' },
  { key: 'listSx', label: 'İşlem Listeleme Değeri', placeholder: 'İşlem listeleme/raporlama servisi için ayrı verilen sx değeri', optional: true },
  { key: 'merchantSecretKey', label: 'Merchant Secret Key', placeholder: 'Hash hesaplamada kullanılan gizli anahtar', type: 'password' },
];

const DEFAULT_INSTALLMENTS = [
  { count: 2, surchargeRate: 0, isActive: false },
  { count: 3, surchargeRate: 0, isActive: false },
  { count: 6, surchargeRate: 2.5, isActive: false },
  { count: 9, surchargeRate: 5.0, isActive: false },
  { count: 12, surchargeRate: 7.5, isActive: false },
];

export default function PaymentProviderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [type, setType] = useState<string>('vakifbank');
  const [environment, setEnvironment] = useState<'test' | 'production'>('test');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [installments, setInstallments] = useState(DEFAULT_INSTALLMENTS);
  const [commissionRate, setCommissionRate] = useState<number>(0);

  useEffect(() => {
    if (isEdit) {
      fetchProvider();
    }
  }, [id]);

  const fetchProvider = async () => {
    try {
      setLoading(true);
      const data = await paymentProviderApi.getById(id!);
      setName(data.name);
      setType(data.type);
      setEnvironment(data.environment);
      setConfig(data.config || {});
      setCommissionRate(data.commissionRate || 0);
      if (data.installmentOptions?.length > 0) {
        // Mevcut taksit seçeneklerini merge et
        const merged = DEFAULT_INSTALLMENTS.map(def => {
          const existing = data.installmentOptions.find(o => o.count === def.count);
          return existing || def;
        });
        setInstallments(merged);
      }
    } catch (error) {
      toast.error('POS sağlayıcı yüklenemedi');
      navigate('/admin/payment-providers');
    } finally {
      setLoading(false);
    }
  };

  const getConfigFields = () => {
    switch (type) {
      case 'vakifbank': return VAKIFBANK_FIELDS;
      case 'iyzico': return IYZICO_FIELDS;
      case 'paynkolay': return PAYNKOLAY_FIELDS;
      default: return VAKIFBANK_FIELDS;
    }
  };

  const handleConfigChange = (key: string, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleInstallmentToggle = (count: number) => {
    setInstallments(prev => prev.map(opt =>
      opt.count === count ? { ...opt, isActive: !opt.isActive } : opt
    ));
  };

  const handleInstallmentRateChange = (count: number, rate: string) => {
    const numRate = parseFloat(rate) || 0;
    setInstallments(prev => prev.map(opt =>
      opt.count === count ? { ...opt, surchargeRate: numRate } : opt
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Sağlayıcı adı gerekli');
      return;
    }

    const fields = getConfigFields();
    for (const field of fields) {
      if (!field.optional && !config[field.key]?.trim()) {
        toast.error(`${field.label} alanı gerekli`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        type: type as PaymentProviderData['type'],
        environment,
        config,
        installmentOptions: installments,
        commissionRate,
      };

      if (isEdit) {
        await paymentProviderApi.update(id!, payload);
        toast.success('POS sağlayıcı güncellendi');
      } else {
        await paymentProviderApi.create(payload);
        toast.success('POS sağlayıcı oluşturuldu');
      }
      navigate('/admin/payment-providers');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <button onClick={() => navigate('/admin/payment-providers')} className="text-blue-600 hover:underline text-sm mb-2">
          ← POS Yönetimi
        </button>
        <h1 className="text-2xl font-bold">{isEdit ? 'POS Sağlayıcı Düzenle' : 'Yeni POS Sağlayıcı'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Genel Bilgiler */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold text-lg mb-4">Genel Bilgiler</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sağlayıcı Adı</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ör: Vakıfbank Sanal POS"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POS Komisyon Oranı (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={commissionRate}
                onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
                placeholder="ör: 1.69"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Banka/POS sağlayıcının her işlemden kestiği komisyon oranı (ör: 1.69 = %1.69). Muhasebe hesaplamalarında kullanılır.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POS Tipi</label>
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setConfig({});
                }}
                disabled={isEdit}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                {PROVIDER_TYPES.map(pt => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ortam</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="test"
                    checked={environment === 'test'}
                    onChange={() => setEnvironment('test')}
                  />
                  <span className="text-sm">Test</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="production"
                    checked={environment === 'production'}
                    onChange={() => setEnvironment('production')}
                  />
                  <span className="text-sm">Canlı (Production)</span>
                </label>
              </div>
              {environment === 'test' && (
                <p className="text-xs text-yellow-600 mt-1">⚠️ Test ortamında gerçek ödeme alınmaz.</p>
              )}
            </div>
          </div>
        </div>

        {/* Credential'lar */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold text-lg mb-4">
            {PROVIDER_TYPES.find(p => p.value === type)?.label} Ayarları
          </h2>

          <div className="space-y-4">
            {getConfigFields().map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}{field.optional && <span className="text-gray-400 font-normal"> (opsiyonel)</span>}
                </label>
                <input
                  type={field.type || 'text'}
                  value={config[field.key] || ''}
                  onChange={(e) => handleConfigChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Taksit Ayarları */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold text-lg mb-2">Taksit Seçenekleri</h2>
          <p className="text-sm text-gray-500 mb-4">Her taksit seçeneği için vade farkı oranı belirleyebilirsiniz.</p>

          <div className="space-y-3">
            {installments.map(opt => (
              <div key={opt.count} className="flex items-center gap-4 p-3 border rounded-lg">
                <label className="flex items-center gap-2 min-w-[120px]">
                  <input
                    type="checkbox"
                    checked={opt.isActive}
                    onChange={() => handleInstallmentToggle(opt.count)}
                    className="w-4 h-4"
                  />
                  <span className="font-medium">{opt.count} Taksit</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Vade Farkı:</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="50"
                    value={opt.surchargeRate}
                    onChange={(e) => handleInstallmentRateChange(opt.count, e.target.value)}
                    disabled={!opt.isActive}
                    className="w-20 border rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                {opt.isActive && opt.surchargeRate > 0 && (
                  <span className="text-xs text-gray-500">
                    (100₺ ürün → {(100 + (100 * opt.surchargeRate / 100)).toFixed(2)}₺ toplam)
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {saving ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Oluştur')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/payment-providers')}
            className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 transition"
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}
